import { type FC, lazy, Suspense, useEffect, useMemo, useRef, useState } from 'react';

import { AT_UNIT_METERS, formatDistance, formatWalk, haversineMeters, type LatLng, mapsDeepLink, parsePuCoordinate } from './geo';
import { buildStaticMapUrl, MAPBOX_TOKEN, prefersLightweightMap, readBrandHex, readInkHex } from './static-map-url';
import {
	ActionButton,
	ActionLink,
	Actions,
	Caption,
	FallbackPane,
	Legend,
	LegendDot,
	MapFigure,
	QuietNote,
	SnapshotFrame,
} from './UnitDistanceMap.styled';
import { useDocumentTheme } from './use-document-theme';
import { useWalkingRoute } from './use-walking-route';

// "You ↔ this polling unit" — a static Mapbox snapshot with a one-sentence
// caption, shown directly under the Polling Unit accordion once a unit is
// chosen. It is a decision aid, never the picker: the LGA / Ward / PU choice
// stays exactly as it was. Leaflet loads only on "Explore map".

const LeafletPairMap = lazy(() => import('./LeafletPairMap'));

/** The slice of a polling-unit row this card reads. */
export interface UnitDistanceMapPu {
	id: number;
	name: string;
	latitude: string | null;
	longitude: string | null;
}

export interface UnitDistanceMapProps {
	/** The chosen polling unit, or null when none (or a ward / LGA) is selected. */
	pu: UnitDistanceMapPu | null;
	/** The applicant's fix, read from the location form (never re-requested here). */
	userLatitude: number | null | undefined;
	userLongitude: number | null | undefined;
	/** Horizontal accuracy of that fix in metres, when known. */
	accuracyMeters?: number | null;
}

export const UNMAPPED_UNIT_COPY =
	'Map not available for this unit. INEC hasn’t published its coordinates yet. You can still choose it.';
export const PREVIEW_FAILED_COPY = 'Map preview couldn’t load.';
export const APPROXIMATE_ACCURACY_METERS = 500;
export const SNAPSHOT_MAX_HEIGHT = 240;
export const WIDTH_STEP = 40;
// Mapbox caps the raster at 1280 px, so @2x is only safe up to a 640 px box.
export const RETINA_MAX_WIDTH = 640;

let warnedMissingToken = false;
/** Test hook: let the next mount without a token warn again. */
export const __resetMissingTokenWarning = (): void => {
	warnedMissingToken = false;
};

const isFinitePair = (lat: number | null | undefined, lng: number | null | undefined): lat is number =>
	typeof lat === 'number' && typeof lng === 'number' && Number.isFinite(lat) && Number.isFinite(lng);

const roundToStep = (value: number): number => Math.max(WIDTH_STEP, Math.round(value / WIDTH_STEP) * WIDTH_STEP);

export const distanceCaption = (meters: number): string =>
	meters < AT_UNIT_METERS ? formatDistance(meters) : `${formatDistance(meters)} away (straight line)`;

export const snapshotAltText = (puName: string, meters: number): string =>
	meters < AT_UNIT_METERS
		? `Map of your location and ${puName}. You are at the polling unit.`
		: `Map of your location and ${puName}, ${formatDistance(meters).replace(/^About /, 'about ')} apart in a straight line.`;

const UnitDistanceMap: FC<UnitDistanceMapProps> = ({ pu, userLatitude, userLongitude, accuracyMeters = null }) => {
	const user = useMemo<LatLng | null>(
		() => (isFinitePair(userLatitude, userLongitude) ? { lat: userLatitude, lng: userLongitude! } : null),
		[userLatitude, userLongitude]
	);
	const unit = useMemo<LatLng | null>(() => (pu ? parsePuCoordinate(pu.latitude, pu.longitude) : null), [pu]);
	const meters = user && unit ? haversineMeters(user, unit) : null;
	const atUnit = meters !== null && meters < AT_UNIT_METERS;

	const walk = useWalkingRoute(pu && unit && user && !atUnit ? pu.id : null, user, unit);
	const theme = useDocumentTheme();
	const brandHex = useMemo(() => readBrandHex(), [theme]);
	const inkHex = useMemo(() => readInkHex(), [theme]);
	const [lightweight] = useState(prefersLightweightMap);

	// The step is hidden with `d-none` (not unmounted) until the applicant reaches
	// it, so the first measurements are 0 and are ignored; the image is requested
	// only once the card has a real width.
	const [figureEl, setFigureEl] = useState<HTMLElement | null>(null);
	const [width, setWidth] = useState(0);
	useEffect(() => {
		if (!figureEl || typeof ResizeObserver === 'undefined') return;
		const apply = (raw: number) => {
			if (raw > 0) setWidth((prev) => (roundToStep(raw) === prev ? prev : roundToStep(raw)));
		};
		apply(figureEl.getBoundingClientRect().width);
		const observer = new ResizeObserver((entries) => {
			entries.forEach((entry) => apply(entry.contentRect.width));
		});
		observer.observe(figureEl);
		return () => observer.disconnect();
	}, [figureEl]);

	const [imageFailed, setImageFailed] = useState(false);
	const [exploring, setExploring] = useState(false);
	const exploreButtonRef = useRef<HTMLButtonElement>(null);
	const wasExploring = useRef(false);

	useEffect(() => {
		if (wasExploring.current && !exploring) exploreButtonRef.current?.focus();
		wasExploring.current = exploring;
	}, [exploring]);

	const hasToken = Boolean(MAPBOX_TOKEN);
	const showCard = Boolean(pu && user && unit);
	useEffect(() => {
		if (showCard && !hasToken && !warnedMissingToken) {
			warnedMissingToken = true;
			console.warn('[UnitDistanceMap] REACT_APP_MAPBOX_TOKEN is not set; showing the distance without a map image.');
		}
	}, [showCard, hasToken]);

	// Reset the picker state whenever the pair changes.
	useEffect(() => {
		setImageFailed(false);
		setExploring(false);
	}, [pu?.id, user?.lat, user?.lng]);

	const height = width > 0 ? Math.min(SNAPSHOT_MAX_HEIGHT, Math.round((width * 9) / 16)) : 0;
	const retina = !lightweight && width > 0 && width <= RETINA_MAX_WIDTH;

	const src = useMemo(() => {
		if (!user || !unit || !MAPBOX_TOKEN || width === 0) return null;
		return buildStaticMapUrl({ user, pu: unit, theme, width, height, retina, brandHex, userHex: inkHex, token: MAPBOX_TOKEN });
	}, [user, unit, theme, width, height, retina, brandHex, inkHex]);

	useEffect(() => {
		setImageFailed(false);
	}, [src]);

	// Nothing to say: no unit yet (or a ward / LGA), or the GPS card is still
	// explaining itself above.
	if (!pu || !user) return null;

	if (!unit || meters === null) {
		return (
			<QuietNote
				role='note'
				data-testid='unit-distance-unmapped'
			>
				{UNMAPPED_UNIT_COPY}
			</QuietNote>
		);
	}

	const alt = snapshotAltText(pu.name, meters);
	const approximate =
		typeof accuracyMeters === 'number' && Number.isFinite(accuracyMeters) && accuracyMeters > APPROXIMATE_ACCURACY_METERS;

	return (
		<MapFigure
			ref={setFigureEl}
			data-testid='unit-distance-map'
		>
			{hasToken ? (
				exploring ? (
					<Suspense
						fallback={
							<SnapshotFrame style={{ aspectRatio: `${width || 16} / ${height || 9}` }}>
								<FallbackPane>Loading map…</FallbackPane>
							</SnapshotFrame>
						}
					>
						<LeafletPairMap
							user={user}
							pu={unit}
							puName={pu.name}
							theme={theme}
							onClose={() => setExploring(false)}
						/>
					</Suspense>
				) : (
					<SnapshotFrame style={{ aspectRatio: `${width || 16} / ${height || 9}` }}>
						{src && !imageFailed ? (
							<img
								src={src}
								alt={alt}
								width={width}
								height={height}
								loading='lazy'
								decoding='async'
								referrerPolicy='strict-origin-when-cross-origin'
								onError={() => setImageFailed(true)}
							/>
						) : null}
						{imageFailed ? <FallbackPane role='status'>{PREVIEW_FAILED_COPY}</FallbackPane> : null}
					</SnapshotFrame>
				)
			) : null}

			<Caption aria-live='polite'>
				<p className='distance-line'>
					<span>{distanceCaption(meters)}</span>
					{walk.status === 'ready' ? <span className='walk'> · {formatWalk(walk.seconds)}</span> : null}
				</p>
				{approximate ? <p className='accuracy'>Your phone’s location is approximate (±{Math.round(accuracyMeters)} m).</p> : null}
			</Caption>

			{hasToken ? (
				<Legend aria-label='Map legend'>
					<li>
						<LegendDot
							tone='you'
							aria-hidden='true'
						/>
						You
					</li>
					<li>
						<LegendDot
							tone='unit'
							aria-hidden='true'
						/>
						Polling unit
					</li>
				</Legend>
			) : null}

			<Actions>
				<ActionLink
					href={mapsDeepLink(unit)}
					target='_blank'
					rel='noopener noreferrer'
				>
					Open in Maps
				</ActionLink>
				{hasToken && !exploring ? (
					<ActionButton
						ref={exploreButtonRef}
						type='button'
						onClick={() => setExploring(true)}
						aria-expanded={exploring}
					>
						Explore map
					</ActionButton>
				) : null}
			</Actions>
		</MapFigure>
	);
};

export default UnitDistanceMap;
